import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Button, ProgressBar, Form, Card, Row, Col } from "react-bootstrap";
import { Modal } from "react-responsive-modal";
import { useToasts } from "react-toast-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, faArrowRight, faCheckCircle, faComment,
  faFile, faMicrophone, faVideo, faClipboard, faList, faEdit, faQuestion,
  faUserFriends
} from "@fortawesome/free-solid-svg-icons";
import Loader from "components/Loader";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import UserDefaultAvatar from "assets/img/user.svg";
import CourseService from "services/CourseService";
import TeamService from "services/TeamService";
import { makeUrlsClickable } from "utils/helpers";
import { useUserContext } from "store/contexts/userContext";

const UserCourseView = ({ 
  isGrowthPartnerView = false, 
  hideProgress = false, 
  hideCompletionStatus = false,
  hideModuleList = false,
  customGrowthPartnerSection = null
}) => {
  const { id, teamId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToasts();
  const { user } = useUserContext();
  
  // Extract userId from query parameters
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('user_id');

  // Helper functions to extract comment and color from team_comments
  const getLessonComment = useCallback((lesson) => {
    if (!lesson || !lesson.team_comments || lesson.team_comments.length === 0) {
      return null;
    }
    // Filter comments for the current team, then get the most recent one
    const teamComments = lesson.team_comments.filter(comment => 
      String(comment.team_id) === String(teamId) && !comment.deleted_at
    );
    
    if (teamComments.length === 0) {
      return null;
    }
    
    // Get the most recent comment (assuming they're ordered by creation date)
    const mostRecentComment = teamComments[teamComments.length - 1];
    return mostRecentComment.comment;
  }, [teamId]);

  const getLessonColor = useCallback((lesson) => {
    if (!lesson || !lesson.team_comments || lesson.team_comments.length === 0) {
      return null;
    }
    // Filter comments for the current team, then get the most recent one
    const teamComments = lesson.team_comments.filter(comment => 
      String(comment.team_id) === String(teamId) && !comment.deleted_at
    );
    
    if (teamComments.length === 0) {
      return null;
    }
    
    // Get the most recent comment (assuming they're ordered by creation date)
    const mostRecentComment = teamComments[teamComments.length - 1];
    return mostRecentComment.color;
  }, [teamId]);

  const hasLessonBeenReviewed = useCallback((lesson) => {
    const comment = getLessonComment(lesson);
    const color = getLessonColor(lesson);
    return !!(comment && color);
  }, [getLessonComment, getLessonColor]);

  const getLessonUserFeedback = useCallback((lesson) => {
    if (!lesson || !lesson.team_comments || lesson.team_comments.length === 0) {
      return null;
    }
    // Filter comments for the current team, then get the most recent one
    const teamComments = lesson.team_comments.filter(comment => 
      String(comment.team_id) === String(teamId) && !comment.deleted_at
    );
    
    if (teamComments.length === 0) {
      return null;
    }
    
    // Get the most recent comment (assuming they're ordered by creation date)
    const mostRecentComment = teamComments[teamComments.length - 1];
    return mostRecentComment.user_feedback;
  }, [teamId]);

  const hasUserFeedbackBeenSubmitted = useCallback((lesson) => {
    const userFeedback = getLessonUserFeedback(lesson);
    return !!userFeedback;
  }, [getLessonUserFeedback]);
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [courseProgress, setCourseProgress] = useState(null);
  const [showGrowthPartnerModal, setShowGrowthPartnerModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [selectedGrowthPartner, setSelectedGrowthPartner] = useState(null);
  const [growthPartner, setGrowthPartner] = useState(null);
  const [canViewLessonResponses, setCanViewLessonResponses] = useState(false);
  const [loadingGrowthPartnerDetails, setLoadingGrowthPartnerDetails] = useState(false);
  const [siaStatus, setSiaStatus] = useState("");
  const [reflectionText, setReflectionText] = useState("");
  const [userFeedback, setUserFeedback] = useState("");
  const [submittingReviewerData, setSubmittingReviewerData] = useState(false);
  const [submittingUserFeedback, setSubmittingUserFeedback] = useState(false);
  
  // Add state for user responses
  const [userLessonResponses, setUserLessonResponses] = useState([]);
  const [currentLessonResponse, setCurrentLessonResponse] = useState(null);
  
  const [completedModules, setCompletedModules] = useState(() => {
    const saved = localStorage.getItem(`userCourse_completed_${teamId}_${id}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [completedLessons, setCompletedLessons] = useState(() => {
    const saved = localStorage.getItem(`userCourse_completedLessons_${teamId}_${id}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [courseFinished, setCourseFinished] = useState(() => {
    const saved = localStorage.getItem(`userCourse_finished_${teamId}_${id}`);
    return saved === 'true';
  });
  
  const [lastVisitedIndex] = useState(() => {
    const saved = localStorage.getItem(`userCourse_lastIndex_${teamId}_${id}`);
    return saved ? Number(saved) : 0;
  });
  
  const [activeModuleIndex, setActiveModuleIndex] = useState(lastVisitedIndex);
  const [activeLessonIndex, setActiveLessonIndex] = useState(-1);
  const [responseType, setResponseType] = useState("text");
  const [textResponse, setTextResponse] = useState("");
  const [responseAttachment, setResponseAttachment] = useState(null);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [hasUserInteractedWithResponse, setHasUserInteractedWithResponse] = useState(false);

  const areAllLessonsInModuleCompleted = useCallback((moduleIndex) => {
    if (!course || !course.modules[moduleIndex] || !course.modules[moduleIndex].lessons) {
      return false;
    }
    
    const module = course.modules[moduleIndex];
    return module.lessons.every((lesson) => {
      const key = `${moduleIndex}-${lesson.id}`;
      return completedLessons.includes(key);
    });
  }, [course, completedLessons]);
  
  const isLessonCompleted = useCallback((moduleIndex, lessonId) => {
    return completedLessons.includes(`${moduleIndex}-${lessonId}`);
  }, [completedLessons]);
  
  const canCompleteLesson = useCallback((moduleIndex, lessonIndex) => {
    if (!course) return false;
    
    if (lessonIndex > 0) {
      const previousLesson = course.modules[moduleIndex].lessons[lessonIndex - 1];
      if (!isLessonCompleted(moduleIndex, previousLesson.id)) {
        return false;
      }
    }
    
    for (let i = 0; i < moduleIndex; i++) {
      if (!areAllLessonsInModuleCompleted(i)) {
        return false;
      }
    }
    
    return true;
  }, [course, isLessonCompleted, areAllLessonsInModuleCompleted]);
  
  const isGrowthPartner = useCallback(() => {
    if (isGrowthPartnerView) return true;
    
    if (!growthPartner || !user) return false;
    return String(user.id) === String(growthPartner.id);
  }, [growthPartner, user, isGrowthPartnerView]);
  
  const getCompletedLessonsInModuleCount = useCallback((moduleIndex) => {
    if (!course || !course.modules[moduleIndex] || !course.modules[moduleIndex].lessons) {
      return 0;
    }
    
    const module = course.modules[moduleIndex];
    return module.lessons.reduce((count, lesson) => {
      const key = `${moduleIndex}-${lesson.id}`;
      return completedLessons.includes(key) ? count + 1 : count;
    }, 0);
  }, [course, completedLessons]);

  const getCompletedModulesCount = useCallback(() => {
    if (!course) return 0;
    
    if (courseProgress && courseProgress.module_progress) {
      return courseProgress.module_progress.filter(module => module.completed).length;
    }
    
    return completedModules.length;
  }, [course, courseProgress, completedModules]);
  
  const calculateOverallProgress = useCallback(() => {
    if (!course) return 0;
    
    if (courseProgress && courseProgress.course_progress) {
      return courseProgress.course_progress.progress_percentage;
    }
    
    if (course.modules.length === 0) return 0;
    return Math.round((getCompletedModulesCount() / course.modules.length) * 100);
  }, [course, courseProgress, getCompletedModulesCount]);

  useEffect(() => {
    if (growthPartner) {
      setSelectedGrowthPartner(growthPartner);
    }
  }, [growthPartner]);

  useEffect(() => {
    localStorage.setItem(`userCourse_completed_${teamId}_${id}`,
      JSON.stringify(completedModules));
    
    if (course) {
      const userProgress = JSON.parse(localStorage.getItem(`userCourseProgress_${teamId}`) || "{}");
      userProgress[id] = {
        completedModules,
        totalModules: course.modules.length
      };
      localStorage.setItem(`userCourseProgress_${teamId}`, JSON.stringify(userProgress));
    }
  }, [completedModules, id, teamId, course]);

  useEffect(() => {
    localStorage.setItem(`userCourse_completedLessons_${teamId}_${id}`,
      JSON.stringify(completedLessons));
  }, [completedLessons, id, teamId]);

  useEffect(() => {
    localStorage.setItem(`userCourse_lastIndex_${teamId}_${id}`,
      String(activeModuleIndex));
  }, [activeModuleIndex, id, teamId]);

  useEffect(() => {
    setActiveModuleIndex(lastVisitedIndex);
    setActiveLessonIndex(-1);
  }, [course, lastVisitedIndex]);

  // Add effect to load saved responses when activeLessonIndex changes
  useEffect(() => {
    setSiaStatus("");
    setReflectionText("");
    setUserFeedback("");
    
    if (activeLessonIndex >= 0 && course && course.modules[activeModuleIndex] && course.modules[activeModuleIndex].lessons[activeLessonIndex]) {
      const lessonKey = `${activeModuleIndex}-${course.modules[activeModuleIndex].lessons[activeLessonIndex].id}`;
      const savedResponses = JSON.parse(sessionStorage.getItem(`userLessonResponses_${teamId}_${id}`) || '{}');
      
      if (savedResponses[lessonKey]) {
        const savedData = savedResponses[lessonKey];
        setTextResponse(savedData.textResponse || "");
        setResponseType(savedData.responseType || "text");
        setHasUserInteractedWithResponse(savedData.hasUserInteractedWithResponse || false);
      }
      
      const activeLesson = course.modules[activeModuleIndex].lessons[activeLessonIndex];
      if (activeLesson.review === true) {
        const lessonColor = getLessonColor(activeLesson);
        const lessonComment = getLessonComment(activeLesson);
        const lessonUserFeedback = getLessonUserFeedback(activeLesson);
        
        if (lessonColor) {
          setSiaStatus(lessonColor);
        }
        
        if (lessonComment) {
          setReflectionText(lessonComment);
        }
        
        if (lessonUserFeedback) {
          setUserFeedback(lessonUserFeedback);
        }
      }
    }
  }, [activeLessonIndex, activeModuleIndex, course, teamId, id, getLessonColor, getLessonComment, getLessonUserFeedback]);
  
  useEffect(() => {
    if (course && course.modules.every((_, index) => areAllLessonsInModuleCompleted(index))) {
      setCourseFinished(true);
      localStorage.setItem(`userCourse_finished_${teamId}_${id}`, 'true');
    }
  }, [course, completedLessons, id, teamId, areAllLessonsInModuleCompleted]);
  
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        
        const courseResponse = await CourseService.getCourse(teamId, id, userId);
        
        if (courseResponse && courseResponse.data) {
          setCourse(courseResponse.data.data);
          
          if (courseResponse.data.data.growth_partner) {
            setGrowthPartner(courseResponse.data.data.growth_partner);
          }
        } else {
          throw new Error('Course not found');
        }
        
        if (!isGrowthPartnerView) {
          const progressResponse = await CourseService.getCourseProgress(teamId, id);
          
          if (progressResponse && progressResponse.data) {
            setCourseProgress(progressResponse.data.data);
            
            if (progressResponse.data.data.module_progress) {
              const completedModuleIds = progressResponse.data.data.module_progress
                .filter(module => module.completed)
                .map(module => {
                  const moduleIndex = courseResponse.data.data.modules.findIndex(m => m.id === module.module_id);
                  return moduleIndex;
                })
                .filter(index => index !== -1);
              
              setCompletedModules(completedModuleIds);
            }
            
            if (progressResponse.data.data.lesson_responses) {
              // Store lesson responses from API
              setUserLessonResponses(progressResponse.data.data.lesson_responses);
              
              const completedLessonKeys = progressResponse.data.data.lesson_responses
                .filter(lesson => lesson.completed)
                .map(lesson => {
                  const moduleIndex = courseResponse.data.data.modules.findIndex(m => m.id === lesson.module_id);
                  if (moduleIndex !== -1) {
                    return `${moduleIndex}-${lesson.lesson_id}`;
                  }
                  return null;
                })
                .filter(key => key !== null);
              
              setCompletedLessons(completedLessonKeys);
            }
            
            if (progressResponse.data.data.course_progress && 
                progressResponse.data.data.course_progress.completed) {
              setCourseFinished(true);
              localStorage.setItem(`userCourse_finished_${teamId}_${id}`, 'true');
            }
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching course data:", error);
        addToast("Failed to load course data", { appearance: "error" });
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id, teamId, addToast, isGrowthPartnerView, userId]);

  // Add effect to find and set the current lesson response when activeLessonIndex changes
  useEffect(() => {
    if (activeLessonIndex >= 0 && course) {
      const activeLesson = course.modules[activeModuleIndex].lessons[activeLessonIndex];
      let response = null;
      
      if (isGrowthPartner()) {
        // For growth partners, look in course.lesson_responses
        if (course.lesson_responses && course.lesson_responses.length > 0) {
          response = course.lesson_responses.find(
            resp => resp.lesson_id === activeLesson.id && resp.module_id === course.modules[activeModuleIndex].id
          );
        }
      } else {
        // For regular users, look in userLessonResponses (from course progress API)
        if (userLessonResponses.length > 0) {
          response = userLessonResponses.find(
            resp => resp.lesson_id === activeLesson.id && resp.module_id === course.modules[activeModuleIndex].id
          );
        }
      }
      
      setCurrentLessonResponse(response || null);
    } else {
      setCurrentLessonResponse(null);
    }
  }, [activeLessonIndex, activeModuleIndex, course, userLessonResponses, isGrowthPartner]);
  
  const mainContentRef = useRef(null);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoadingTeamMembers(true);
      const response = await TeamService.getMembers(teamId);
      
      if (response && response.data && response.data.data) {
        let filteredMembers = response.data.data;
        if (user?.id) {
          filteredMembers = filteredMembers.filter(member => {
            if (growthPartner && String(member.id) === String(growthPartner.id)) {
              return true;
            }
            return String(member.id) !== String(user.id);
          });
        }
        setTeamMembers(filteredMembers);
      }
      setLoadingTeamMembers(false);
    } catch (error) {
      console.error("Error fetching team members:", error);
      addToast("Failed to load team members", { appearance: "error" });
      setLoadingTeamMembers(false);
    }
  }, [teamId, user?.id, addToast, growthPartner]);

  const handleOpenGrowthPartnerModal = async () => {
    fetchTeamMembers();
    if (growthPartner) {
      setSelectedGrowthPartner(growthPartner);
    }
    setShowGrowthPartnerModal(true);
    
    // Fetch growth partner details to get can_view_lesson_responses setting
    try {
      setLoadingGrowthPartnerDetails(true);
      const response = await CourseService.getGrowthPartnerDetails(teamId, course.id);
      if (response && response.data && response.data.data) {
        setCanViewLessonResponses(response.data.data.can_view_lesson_responses || false);
      }
    } catch (error) {
      console.error("Error fetching growth partner details:", error);
      // Don't show error toast as this is not critical for the modal to function
      setCanViewLessonResponses(false);
    } finally {
      setLoadingGrowthPartnerDetails(false);
    }
  };

  const handleCloseGrowthPartnerModal = () => {
    setShowGrowthPartnerModal(false);
    setSelectedGrowthPartner(null);
    setCanViewLessonResponses(false);
  };

  const handleGrowthPartnerSelect = (member) => {
    setSelectedGrowthPartner(member);
  };

  const handleConfirmGrowthPartner = async () => {
    if (selectedGrowthPartner) {
      try {
        await CourseService.assignGrowthPartner(course.id, teamId, selectedGrowthPartner.id, canViewLessonResponses);
        
        setGrowthPartner(selectedGrowthPartner);
        setShowGrowthPartnerModal(false);
        
        const partnerName = selectedGrowthPartner.name || selectedGrowthPartner.full_name;
        const visibilityText = canViewLessonResponses ? " and can view your lesson responses" : " but cannot view your lesson responses";
        addToast(`${partnerName} has been assigned as your growth partner for this course${visibilityText}`, { appearance: "success" });
      } catch (error) {
        console.error("Error assigning growth partner:", error);
        addToast("Failed to assign growth partner", { appearance: "error" });
      }
    }
  };

  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleNextModule = () => {
    if (activeModuleIndex < course.modules.length - 1) {
      setActiveModuleIndex(activeModuleIndex + 1);
      setActiveLessonIndex(-1);
      setShowResponseForm(false);
      resetResponseData();
      setSiaStatus("");
      setReflectionText("");
      setUserFeedback("");
      scrollToTop();
    }
  };
  
  const handlePreviousModule = () => {
    if (activeModuleIndex > 0) {
      setActiveModuleIndex(activeModuleIndex - 1);
      setActiveLessonIndex(-1);
      setShowResponseForm(false);
      resetResponseData();
      setSiaStatus("");
      setReflectionText("");
      setUserFeedback("");
      scrollToTop();
    }
  };
  
  const handleGoToModule = (index) => {
    setActiveModuleIndex(index);
    setActiveLessonIndex(-1);
    setShowResponseForm(false)
    resetResponseData();
    setSiaStatus("");
    setReflectionText("");
    setUserFeedback("");
    scrollToTop();
  };

  const handleGoToLesson = (lessonIndex) => {
    if (activeLessonIndex >= 0) {
      const currentLessonKey = `${activeModuleIndex}-${activeModule.lessons[activeLessonIndex].id}`;
      const responseData = {
        textResponse,
        responseType,
        hasUserInteractedWithResponse,
        hasAttachment: !!responseAttachment
      };
      
      const savedResponses = JSON.parse(sessionStorage.getItem(`userLessonResponses_${teamId}_${id}`) || '{}');
      savedResponses[currentLessonKey] = responseData;
      sessionStorage.setItem(`userLessonResponses_${teamId}_${id}`, JSON.stringify(savedResponses));
    }

    setActiveLessonIndex(lessonIndex);
    setShowResponseForm(false);
    
    setTextResponse("");
    setResponseAttachment(null);
    setResponseType("text");
    setHasUserInteractedWithResponse(false);
    
    setSiaStatus("");
    setReflectionText("");
    setUserFeedback("");
    
    setTimeout(() => {
      if (course && course.modules[activeModuleIndex] && course.modules[activeModuleIndex].lessons[lessonIndex]) {
        const newLessonKey = `${activeModuleIndex}-${course.modules[activeModuleIndex].lessons[lessonIndex].id}`;
        const savedResponses = JSON.parse(sessionStorage.getItem(`userLessonResponses_${teamId}_${id}`) || '{}');
        
        if (savedResponses[newLessonKey]) {
          const savedData = savedResponses[newLessonKey];
          setTextResponse(savedData.textResponse || "");
          setResponseType(savedData.responseType || "text");
          setHasUserInteractedWithResponse(savedData.hasUserInteractedWithResponse || false);
        }
        
        // Only set reviewer data if the lesson has existing review data
        const activeLesson = course.modules[activeModuleIndex].lessons[lessonIndex];
        if (activeLesson.review === true) {
          const lessonColor = getLessonColor(activeLesson);
          const lessonComment = getLessonComment(activeLesson);
          const lessonUserFeedback = getLessonUserFeedback(activeLesson);
          
          // Only set color if it exists in the lesson data
          if (lessonColor) {
            setSiaStatus(lessonColor);
          }
          
          // Only set comment if it exists in the lesson data
          if (lessonComment) {
            setReflectionText(lessonComment);
          }
          
          // Only set user feedback if it exists in the lesson data
          if (lessonUserFeedback) {
            setUserFeedback(lessonUserFeedback);
          }
        }
      }
      
      scrollToTop();
    }, 0);
  };

  const handleBackToModuleOverview = () => {
    setActiveLessonIndex(-1);
    setShowResponseForm(false);
    resetResponseData();
    // Reset reviewer section data
    setSiaStatus("");
    setReflectionText("");
    setUserFeedback("");
    setTimeout(() => {
      scrollToTop();
    }, 0);
  };
  
  const resetResponseData = () => {
    if (activeLessonIndex >= 0) {
      const lessonKey = `${activeModuleIndex}-${activeModule.lessons[activeLessonIndex].id}`;
      const responseData = {
        textResponse,
        responseType,
        hasUserInteractedWithResponse,
        hasAttachment: !!responseAttachment
      };
      
      const savedResponses = JSON.parse(sessionStorage.getItem(`userLessonResponses_${teamId}_${id}`) || '{}');
      savedResponses[lessonKey] = responseData;
      sessionStorage.setItem(`userLessonResponses_${teamId}_${id}`, JSON.stringify(savedResponses));
    }
    
    setTextResponse("");
    setResponseAttachment(null);
    setResponseType("text");
    setHasUserInteractedWithResponse(false);
    setSiaStatus("");
    setReflectionText("");
    setUserFeedback("");
  };

  const handleMarkLessonAsCompleted = async (lessonId) => {
    const lessonKey = `${activeModuleIndex}-${lessonId}`;
    const activeModule = course.modules[activeModuleIndex];
    
    try {
      await CourseService.completeLesson(
        course.id,
        activeModule.id,
        lessonId,
        teamId
      );
      
      if (!completedLessons.includes(lessonKey)) {
        setCompletedLessons([...completedLessons, lessonKey]);

        const updatedCompletedLessons = [...completedLessons, lessonKey];
        const allLessonsCompleted = activeModule.lessons.every((lesson) => {
          const key = `${activeModuleIndex}-${lesson.id}`;
          return updatedCompletedLessons.includes(key);
        });

        if (allLessonsCompleted && !completedModules.includes(activeModuleIndex)) {
          setCompletedModules([...completedModules, activeModuleIndex]);
        }
      }
      
      const progressResponse = await CourseService.getCourseProgress(teamId, id);
      if (progressResponse && progressResponse.data) {
        setCourseProgress(progressResponse.data.data);
      }
      
      addToast("Lesson completed successfully", { appearance: "success" });
    } catch (error) {
      console.error("Error completing lesson:", error);
      addToast("Failed to mark lesson as completed", { appearance: "error" });
    }
  };

  const handleFinish = async () => {
    try {
      let allCompleted = true;
      for (let i = 0; i < course.modules.length; i++) {
        if (!areAllLessonsInModuleCompleted(i)) {
          allCompleted = false;
          break;
        }
      }
      
      if (!allCompleted) {
        addToast("Please complete all lessons before finishing the course", { appearance: "warning" });
        return;
      }
      
      localStorage.setItem(`userCourse_finished_${teamId}_${id}`, 'true');
      setCourseFinished(true);
      
      try {
        const progressResponse = await CourseService.getCourseProgress(teamId, id);
        if (progressResponse && progressResponse.data) {
          setCourseProgress(progressResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching final course progress:", error);
      }
      
      addToast("Course completed! Your progress has been saved.", { appearance: "success" });
      history.push(`/teams/${teamId}/courses`);
    } catch (error) {
      console.error("Error finishing course:", error);
      addToast("Failed to complete the course", { appearance: "error" });
    }
  };

  const handleResponseTypeChange = (type) => {
    setResponseType(type);
    
    if (type === 'text') {
      setHasUserInteractedWithResponse(textResponse.trim() !== '');
    } else {
      setHasUserInteractedWithResponse(!!responseAttachment);
    }
    
    if (type !== 'text') {
      setTextResponse('');
    }
    if (responseAttachment && type !== 'file' && type !== 'video' && type !== 'audio') {
      setResponseAttachment(null);
    }
  };

  const handleAttachmentSelected = (file) => {
    setResponseAttachment(file);
    setHasUserInteractedWithResponse(!!file);
  };

  const handleClearAttachment = () => {
    setResponseAttachment(null);
    if (responseType === 'text' ? !textResponse.trim() : true) {
      setHasUserInteractedWithResponse(false);
    }
  };

  const handleTextResponseChange = (e) => {
    setTextResponse(e.target.value);
    if (e.target.value.trim() !== '') {
      setHasUserInteractedWithResponse(true);
    } else {
      setHasUserInteractedWithResponse(false);
    }
  };
  
  const handleSiaStatusChange = (status) => {
    setSiaStatus(status);
  };
  
  const handleReflectionTextChange = (e) => {
    setReflectionText(e.target.value);
  };

  const handleUserFeedbackChange = (e) => {
    setUserFeedback(e.target.value);
  };
  
  // Add helper function to get S3 URL for file responses
  const getResponseFileUrl = (response) => {
    if (!response) return null;
    
    if (response.response_type === 'text') {
      return null;
    }
    
    // For file, audio, video, image responses, add the complete S3 URL
    if (response.response_content && response.response_url) {
      return response.response_url;
    }
    
    return response.response_content;
  };

  const handleSubmitResponse = async () => {
    if (activeLessonIndex >= 0 && !canCompleteLesson(activeModuleIndex, activeLessonIndex)) {
      addToast("You must complete previous lessons first", { appearance: "error" });
      return;
    }
    
    if (responseType === 'text' && !textResponse.trim()) {
      addToast("Please enter your text response", { appearance: "error" });
      return;
    }
    
    if ((responseType === 'file' || responseType === 'video' || responseType === 'audio') && !responseAttachment) {
      addToast(`Please select a ${responseType} file to upload`, { appearance: "error" });
      return;
    }
    
    setSubmittingResponse(true);
    
    try {
      const activeModule = course.modules[activeModuleIndex];
      const activeLesson = activeModule.lessons[activeLessonIndex];
      
      const responseData = {
        course_id: course.id,
        module_id: activeModule.id,
        lesson_id: activeLesson.id,
        response_type: responseType,
        team_id: teamId
      };
      
      if (responseType === 'text') {
        responseData.response_content = textResponse;
      } else {
        responseData.response_file = responseAttachment;
      }
      
      const response = await CourseService.submitLessonResponse(responseData);
      
      await handleMarkLessonAsCompleted(activeLesson.id);
      
      // If response was successful, update the userLessonResponses state
      if (response && response.data && response.data.data) {
        const newResponse = response.data.data;
        // Ensure completed_at is properly set
        const responseWithTimestamp = {
          ...newResponse,
          completed_at: newResponse.completed_at || new Date().toISOString()
        };
        
        setUserLessonResponses(prevResponses => {
          // Check if we already have a response for this lesson
          const existingIndex = prevResponses.findIndex(
            resp => resp.lesson_id === activeLesson.id && resp.module_id === activeModule.id
          );
          
          if (existingIndex >= 0) {
            // Replace existing response
            const updatedResponses = [...prevResponses];
            updatedResponses[existingIndex] = responseWithTimestamp;
            return updatedResponses;
          } else {
            // Add new response
            return [...prevResponses, responseWithTimestamp];
          }
        });
        
        // Set current lesson response with proper completed_at timestamp
        setCurrentLessonResponse(responseWithTimestamp);
      } else {
        // Refresh course progress to get updated responses
        const progressResponse = await CourseService.getCourseProgress(teamId, id);
        if (progressResponse && progressResponse.data && progressResponse.data.data.lesson_responses) {
          setUserLessonResponses(progressResponse.data.data.lesson_responses);
        }
      }
      
      addToast("Response submitted successfully", { appearance: "success" });
      setShowResponseForm(false);
      setTextResponse("");
      setResponseAttachment(null);
      setResponseType("text");
      setHasUserInteractedWithResponse(false);
      
      const lessonKey = `${activeModuleIndex}-${activeLesson.id}`;
      const savedResponses = JSON.parse(sessionStorage.getItem(`userLessonResponses_${teamId}_${id}`) || '{}');
      if (savedResponses[lessonKey]) {
        delete savedResponses[lessonKey];
        sessionStorage.setItem(`userLessonResponses_${teamId}_${id}`, JSON.stringify(savedResponses));
      }
      
    } catch (error) {
      console.error("Error submitting response:", error);
      addToast("Failed to submit response", { appearance: "error" });
    } finally {
      setSubmittingResponse(false);
    }
  };
  
  const handleSubmitReviewerData = async () => {
    // Only check if user is a growth partner, don't check if the lesson is completed
    if (!isGrowthPartner()) {
      addToast("Only the assigned growth partner can submit review data", { appearance: "error" });
      return;
    }
    
    // Validate that both fields are filled
    if (!siaStatus) {
      addToast("Please select a SIA status (green, yellow, or red)", { appearance: "warning" });
      return;
    }
    
    if (!reflectionText.trim()) {
      addToast("Please enter your reflection or feedback", { appearance: "warning" });
      return;
    }
    
    setSubmittingReviewerData(true);
    
    try {
      const activeModule = course.modules[activeModuleIndex];
      const activeLesson = activeModule.lessons[activeLessonIndex];
      
      await CourseService.submitGrowthPartnerFeedback({
        lesson_id: activeLesson.id,
        comment: reflectionText,
        color: siaStatus,
        team_id: teamId
      });
      
      // Update the lesson in the course object by adding to team_comments array
      const updatedCourse = { ...course };
      const updatedLesson = { ...updatedCourse.modules[activeModuleIndex].lessons[activeLessonIndex] };
      
      // Create new team comment object
      const newTeamComment = {
        id: Date.now(), // Temporary ID, this will be replaced when refreshing from API
        lesson_id: activeLesson.id,
        team_id: parseInt(teamId),
        user_id: user.id,
        comment: reflectionText,
        color: siaStatus,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        user: {
          id: String(user.id),
          first_name: user.first_name || user.name?.split(' ')[0] || '',
          last_name: user.last_name || user.name?.split(' ')[1] || '',
          email: user.email,
          avatar: user.avatar
        }
      };
      
      // Add the new comment to the team_comments array
      if (!updatedLesson.team_comments) {
        updatedLesson.team_comments = [];
      }
      updatedLesson.team_comments = [...updatedLesson.team_comments, newTeamComment];
      
      // Update the lesson in the course
      updatedCourse.modules[activeModuleIndex].lessons[activeLessonIndex] = updatedLesson;
      setCourse(updatedCourse);
      
      addToast("Review data submitted successfully", { appearance: "success" });
    } catch (error) {
      console.error("Error submitting reviewer data:", error);
      addToast("Failed to submit reviewer data", { appearance: "error" });
    } finally {
      setSubmittingReviewerData(false);
    }
  };
  
  const handleSubmitUserFeedback = async () => {
    // Only allow course takers (non-growth partners) to submit user feedback
    if (isGrowthPartner()) {
      addToast("Only course takers can submit user feedback", { appearance: "error" });
      return;
    }
    
    // Get the active lesson
    const activeModule = course.modules[activeModuleIndex];
    const activeLesson = activeModule.lessons[activeLessonIndex];
    
    // Check if lesson has been reviewed by growth partner
    if (!hasLessonBeenReviewed(activeLesson)) {
      addToast("Growth partner must review this lesson first", { appearance: "warning" });
      return;
    }
    
    // Check if user feedback has already been submitted
    if (hasUserFeedbackBeenSubmitted(activeLesson)) {
      addToast("You have already submitted user feedback for this lesson", { appearance: "warning" });
      return;
    }
    
    // Validate that user feedback is filled
    if (!userFeedback.trim()) {
      addToast("Please enter your feedback", { appearance: "warning" });
      return;
    }
    
    setSubmittingUserFeedback(true);
    
    try {
      await CourseService.submitGrowthPartnerFeedback({
        lesson_id: activeLesson.id,
        user_feedback: userFeedback,
        team_id: teamId
      });
      
      // Update the lesson in the course object by updating the most recent team comment
      const updatedCourse = { ...course };
      const updatedLesson = { ...updatedCourse.modules[activeModuleIndex].lessons[activeLessonIndex] };
      
      if (updatedLesson.team_comments && updatedLesson.team_comments.length > 0) {
        // Find the most recent comment for this team
        const teamComments = updatedLesson.team_comments.filter(comment => 
          String(comment.team_id) === String(teamId) && !comment.deleted_at
        );
        
        if (teamComments.length > 0) {
          const mostRecentCommentIndex = updatedLesson.team_comments.findIndex(comment => 
            comment.id === teamComments[teamComments.length - 1].id
          );
          
          if (mostRecentCommentIndex !== -1) {
            // Update the existing comment with user_feedback
            updatedLesson.team_comments[mostRecentCommentIndex] = {
              ...updatedLesson.team_comments[mostRecentCommentIndex],
              user_feedback: userFeedback,
              updated_at: new Date().toISOString()
            };
          }
        }
      }
      
      // Update the lesson in the course
      updatedCourse.modules[activeModuleIndex].lessons[activeLessonIndex] = updatedLesson;
      setCourse(updatedCourse);
      
      addToast("User feedback submitted successfully", { appearance: "success" });
    } catch (error) {
      console.error("Error submitting user feedback:", error);
      addToast("Failed to submit user feedback", { appearance: "error" });
    } finally {
      setSubmittingUserFeedback(false);
    }
  };
  
  const handleBackToOverview = () => {
    if (isGrowthPartner()) {
      history.push(`/teams/${teamId}/growth-partner-courses`);
    } else {
      history.push(`/teams/${teamId}/courses`);
    }
  };
  
  if (loading || !course) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  const activeModule = course.modules[activeModuleIndex];
  const activeLesson = activeLessonIndex >= 0 && activeModule.lessons ? activeModule.lessons[activeLessonIndex] : null;
  
  return (
    <div className="d-flex flex-column user-course-view" data-is-growth-partner={isGrowthPartner().toString()}>
      {/* Main Content */}
      <div className="container-fluid flex-grow-1" ref={mainContentRef}>
        <div className="py-4">
          {/* Back to Courses Link */}
          <div className="mb-3">
            <Button 
              variant="link" 
              className="p-0 text-decoration-none" 
              onClick={handleBackToOverview}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> 
              {isGrowthPartner() ? "Back to Growth Partner Courses" : "Back to Courses"}
            </Button>
          </div>

          <Row>
            <Col md={4}>
              <div className="sticky-left-column">
                {/* Course Info */}
                <Card className="mb-4">
                  <Card.Body>
                    <h4 className="mb-3 course-title">{course.title}</h4>
                    <p>{course.description}</p>

                    {!hideProgress && (
                      <div className="mt-4">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Overall Progress</span>
                          <span>{calculateOverallProgress()}%</span>
                        </div>
                        <ProgressBar now={calculateOverallProgress()} variant="success" />
                        
                        {!hideCompletionStatus && (courseFinished || (courseProgress && courseProgress.course_progress && courseProgress.course_progress.completed) || 
                          course.modules.every((_, index) => areAllLessonsInModuleCompleted(index))) && (
                          <div className="text-success mt-2 d-flex align-items-center">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            <small>Course completed</small>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Growth Partner Section */}
                    {customGrowthPartnerSection ? (
                      // Render custom section for growth partner view
                      customGrowthPartnerSection
                    ) : (
                      <div className="mt-4 pt-3 border-top growth-partner-section">
                        <h5 className="mb-3">Growth Partner</h5>
                        {growthPartner ? (
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <img 
                                src={growthPartner.avatar ?? UserDefaultAvatar} 
                                alt={growthPartner.name || growthPartner.full_name}
                                className="rounded-circle growth-partner-avatar"
                                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                              />
                            </div>
                            <div>
                              <div className="fw-bold">{growthPartner.name || growthPartner.full_name}</div>
                              <Button 
                                variant="link" 
                                className="p-0 text-decoration-none" 
                                onClick={handleOpenGrowthPartnerModal}
                              >
                                Change
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-muted mb-2">Select a growth partner who can review and provide feedback on your course progress.</p>
                            <Button 
                              variant="outline-primary" 
                              onClick={handleOpenGrowthPartnerModal}
                              className="d-flex align-items-center"
                            >
                              <FontAwesomeIcon icon={faUserFriends} className="me-2" />
                              Select Growth Partner
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Module List */}
                {!hideModuleList && (
                  <div className="mb-4 modules-list">
                    {course.modules.map((module, moduleIdx) => (
                      <div key={module.id} className="mb-3">
                        <div 
                          className={`p-3 border rounded ${moduleIdx === activeModuleIndex ? 'bg-light' : ''}`} 
                          onClick={() => handleGoToModule(moduleIdx)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              {!hideCompletionStatus && areAllLessonsInModuleCompleted(moduleIdx) ? (
                                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                              ) : (
                                <div className="rounded-circle border me-2" style={{ width: '1rem', height: '1rem' }}></div>
                              )}
                              <div>
                                <div className="fw-bold">{module.title}</div>
                                {!hideProgress && (
                                  <div className="text-muted small">{getCompletedLessonsInModuleCount(moduleIdx)}/{module.lessons?.length || 0} lessons</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Col>
            
            <Col md={8}>
              {activeLessonIndex === -1 ? (
                // Module Overview
                <Card className="mb-4 sticky-card">
                  <Card.Body>
                    {!hideCompletionStatus && course.modules.every((_, index) => areAllLessonsInModuleCompleted(index)) && (
                      <div className="alert alert-success mb-4">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" size="lg" />
                          <div>
                            <strong>Congratulations!</strong> You've completed all lessons in this course.
                            {!courseFinished && (
                              <div className="mt-2">
                                Click the "Finish Course" button below to mark this course as completed and return to your courses.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <h3 className="mb-3">{activeModule.title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: makeUrlsClickable(activeModule.description || '') }}></div>
                    
                    {/* Module lessons list */}
                    <div className="mt-4">
                      <h5 className="mb-3">Lessons</h5>
                      {activeModule.lessons && activeModule.lessons.map((lesson, lessonIdx) => (
                        <div key={lesson.id} className="mb-2">
                          <Button
                            variant="light"
                            className={`lesson-button ${!hideCompletionStatus && isLessonCompleted(activeModuleIndex, lesson.id) ? 'text-success' : ''}`}
                            onClick={() => handleGoToLesson(lessonIdx)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>
                                {!hideCompletionStatus && isLessonCompleted(activeModuleIndex, lesson.id) ? (
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                ) : (
                                  <div className="rounded-circle border me-2 d-inline-block" style={{ width: '1rem', height: '1rem', position: 'relative', top: '2px' }}></div>
                                )}
                                {lesson.title}
                                <div className="small text-muted ms-4 mt-1">
                                  <FontAwesomeIcon 
                                    icon={
                                      lesson.type === 'video' ? faVideo : 
                                      lesson.type === 'text' ? faClipboard :
                                      lesson.type === 'quiz' ? faQuestion :
                                      lesson.type === 'assignment' ? faEdit : 
                                      faFile
                                    } 
                                    className="me-1" 
                                    style={{ fontSize: '0.8rem' }}
                                  /> 
                                  {lesson.type === 'video' ? 'Video' :
                                    lesson.type === 'text' ? 'Text' :
                                    lesson.type === 'quiz' ? 'Quiz' :
                                    lesson.type === 'assignment' ? 'Assignment' : 
                                    'Document'}
                                  {lesson.review && (
                                    <span className={`ms-2 badge ${hasLessonBeenReviewed(lesson) ? 'bg-success' : 'bg-info'}`}>
                                      {hasLessonBeenReviewed(lesson) ? 'Reviewed' : 'Review'}
                                    </span>
                                  )}
                                </div>
                              </span>
                              <span className="lesson-duration">
                                <div className="lesson-duration">
                                  {lesson.duration || '5'} min
                                </div>
                              </span>
                            </div>
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Navigation buttons */}
                    <div className="button-container mt-4">
                      <Button
                        variant="outline-secondary"
                        className="responsive-button"
                        onClick={handlePreviousModule}
                        disabled={activeModuleIndex === 0}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Previous Module
                      </Button>
                      
                      {/* Only show the finish course button for regular users, not growth partners */}
                      {activeModuleIndex < course.modules.length - 1 ? (
                        <Button
                          variant="primary"
                          className="responsive-button ms-auto"
                          onClick={handleNextModule}
                        >
                          Next Module
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                      ) : (
                        /* Only show Finish Course button when all modules are completed and user is not a growth partner */
                        !isGrowthPartner() && !hideCompletionStatus && course.modules.every((_, index) => areAllLessonsInModuleCompleted(index)) ? (
                          <Button
                            variant="success"
                            className="responsive-button ms-auto"
                            onClick={handleFinish}
                            disabled={courseFinished}
                          >
                            {courseFinished ? "Course Completed" : "Finish Course"}
                            <FontAwesomeIcon icon={faCheckCircle} className="ms-2" />
                          </Button>
                        ) : null
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                // Lesson Content
                <Card>
                  <Card.Body>
                    {/* Lesson Navigation Breadcrumb */}
                    <div className="mb-3">
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none" 
                        onClick={handleBackToModuleOverview}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Module
                      </Button>
                    </div>
                    
                    <div className="d-flex align-items-center mb-3">
                      <h3 className="mb-0 me-2">{activeLesson.title}</h3>
                      {activeLesson.review && (
                        <span className={`badge ${hasLessonBeenReviewed(activeLesson) ? 'bg-success' : 'bg-info'}`}>
                          {hasLessonBeenReviewed(activeLesson) ? 'Reviewed' : 'Review Required'}
                        </span>
                      )}
                    </div>
                    
                    {isGrowthPartner() && activeLesson.review && (
                      <div className={`${!hasLessonBeenReviewed(activeLesson) && 'alert alert-info mb-4'}`}>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={!hasLessonBeenReviewed(activeLesson) && faEdit} className="me-2" size="lg" />
                          <div>
                            {!hasLessonBeenReviewed(activeLesson) && (
                              <>
                                <strong>Growth Partner Action Required</strong>
                                <div>This lesson requires your review. Please provide SIA status and feedback in the Reviewer Section below.</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Lesson Content - Don't show for video lessons */}
                    {activeLesson.type !== 'video' && (
                      <div dangerouslySetInnerHTML={{ __html: makeUrlsClickable(typeof activeLesson.content === 'string' ? activeLesson.content || '' : '') }} className="course-content"></div>
                    )}
                    
                    {/* Video Embed */}
                    {activeLesson.type === 'video' && (
                      <div className="video-container mb-4">
                        {activeLesson.content instanceof File ? (
                          // For uploaded videos (content as binary)
                          <video controls className="w-100">
                            <source 
                              src={URL.createObjectURL(activeLesson.content)} 
                              type={activeLesson.content.type || 'video/mp4'} 
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : typeof activeLesson.content === 'object' && activeLesson.content?.url ? (
                          // For server-uploaded videos returning an object with URL
                          <video controls className="w-100">
                            <source 
                              src={activeLesson.content.url} 
                              type={activeLesson.content.mimeType || 'video/mp4'} 
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : activeLesson.content && typeof activeLesson.content === 'string' && (
                          // For external videos (YouTube, Vimeo, etc.)
                          <iframe
                            src={activeLesson.content}
                            title={activeLesson.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ width: '100%', height: '100%' }}
                          ></iframe>
                        )}
                      </div>
                    )}
                    
                    {/* Attachments - Only show for non-video lessons */}
                    {activeLesson.type !== 'video' && activeLesson.attachments && activeLesson.attachments.length > 0 && (
                      <div className="mt-4">
                        <h5>Attachments</h5>
                        {activeLesson.attachments.map((attachment, idx) => (
                          <div key={idx} className="mb-3">
                            {attachment.type === 'video' && (
                              <div className="mb-3">
                                <h5>Video</h5>
                                <div className="video-container">
                                  <video controls width="100%">
                                    <source src={attachment.url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              </div>
                            )}
                            
                            {attachment.type === 'audio' && (
                              <div className="mb-3">
                                <h5>Audio</h5>
                                <audio controls className="w-100">
                                  <source src={attachment.url} type="audio/mp3" />
                                  Your browser does not support the audio tag.
                                </audio>
                              </div>
                            )}
                            
                            {attachment.type === 'image' && (
                              <div className="mb-3">
                                <h5>Image</h5>
                                <img 
                                  src={attachment.url} 
                                  alt={attachment.originalName || "Lesson attachment"}
                                  className="img-fluid rounded"
                                  style={{ maxHeight: '400px' }}
                                />
                              </div>
                            )}
                            
                            {attachment.type !== 'video' && attachment.type !== 'audio' && attachment.type !== 'image' && (
                              <div className="mb-3">
                                <h5>File</h5>
                                <a 
                                  href={attachment.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-secondary"
                                >
                                  <FontAwesomeIcon icon={faFile} className="me-2" />
                                  {attachment.originalName || attachment.fileName || "Download attachment"}
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Instructions */}
                    {activeLesson.cta && (
                      <div className="instructions">
                        <h5>Call To Action</h5>
                        <div dangerouslySetInnerHTML={{ __html: activeLesson.cta }}></div>
                      </div>
                    )}

                    {/* Call to Action */}
                    {/* {showCta && activeLesson && activeLesson.cta && (
                      <div className="call-to-action">
                        <h5>Call to Action</h5>
                        <p>Have a conversation with a colleague about leadership styles</p>
                      </div>
                    )} */}

                    {/* Response section - only show for regular users, not growth partners */}
                    {!showResponseForm && !isLessonCompleted(activeModuleIndex, activeLesson.id) && !isGrowthPartner() ? (
                      <div className="response-container bg-light p-4 rounded shadow-sm">
                        <h5 className="mb-4 border-bottom pb-2">Your Response</h5>
                        
                        {/* Response type selector */}
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          <Button 
                            variant={responseType === 'text' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('text')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faComment} className="me-2" />
                            Text
                          </Button>
                          <Button 
                            variant={responseType === 'audio' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('audio')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faMicrophone} className="me-2" />
                            Audio
                          </Button>
                          <Button 
                            variant={responseType === 'video' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('video')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faVideo} className="me-2" />
                            Video
                          </Button>
                          <Button 
                            variant={responseType === 'file' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('file')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faFile} className="me-2" />
                            File
                          </Button>
                        </div>
                        
                        {responseType === 'text' && (
                          <Form.Group className="mb-4">
                            <Form.Control 
                              as="textarea" 
                              rows={5} 
                              placeholder="Share your thoughts, reflections, or answers here..."
                              onChange={handleTextResponseChange}
                              value={textResponse}
                              className="response-textarea"
                            />
                          </Form.Group>
                        )}
                        
                        {(responseType === 'audio' || responseType === 'video' || responseType === 'file') && (
                          <div className="mb-4">
                            <Form.Label className="mb-2">Upload {responseType}</Form.Label>
                            <div className="d-flex align-items-center flex-wrap gap-2 upload-container p-3 border rounded bg-white">
                              <AttachmentSelection 
                                onChange={handleAttachmentSelected}
                                accepted={responseType === 'audio' ? ['.mp3', '.wav'] : 
                                          responseType === 'video' ? ['.mp4', '.webm'] : 
                                          ['.pdf', '.doc', '.docx', '.jpg', '.png']}
                                allowDocuments={responseType === 'file'}
                              />
                              {responseAttachment && (
                                <AttachmentPreview
                                  commentAttachmentPreview={"moduleAttachmentPreview"}
                                  attachment={responseAttachment}
                                  clearAttachment={handleClearAttachment}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="d-flex justify-content-end mt-4">
                          {hasUserInteractedWithResponse ? (
                            <Button 
                              variant="primary"
                              onClick={handleSubmitResponse}
                              disabled={(responseType === 'text' && !textResponse.trim()) || 
                                      ((responseType === 'audio' || responseType === 'video' || responseType === 'file') && !responseAttachment) ||
                                      submittingResponse}
                              className="px-4 py-2"
                            >
                              {submittingResponse ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  Submit Response
                                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                                </>
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="success"
                              className="mark-complete-btn px-4 py-2"
                              onClick={() => handleMarkLessonAsCompleted(activeLesson.id)}
                              disabled={!canCompleteLesson(activeModuleIndex, activeLessonIndex) || submittingResponse}
                            >
                              {submittingResponse ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Marking...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                  Mark Complete
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Lesson completed indicator - only show for regular users */}
                    {isLessonCompleted(activeModuleIndex, activeLesson.id) && !isGrowthPartner() && (
                      <div className="d-flex justify-content-center my-4">
                        <div className="lesson-completed-tag">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          <span>Lesson Complete!</span>
                        </div>
                      </div>
                    )}
                    
                    {/* User's submitted response section - show when lesson is completed and response_content is not null */}
                    {(() => {
                      const shouldShowForRegularUser = isLessonCompleted(activeModuleIndex, activeLesson.id) && currentLessonResponse && currentLessonResponse.response_content !== null && !isGrowthPartner();
                      const shouldShowForGrowthPartner = isGrowthPartner() && course.lesson_responses && course.lesson_responses.length > 0 && currentLessonResponse && currentLessonResponse.response_content !== null;
                      
                      return shouldShowForRegularUser || shouldShowForGrowthPartner;
                    })() && (
                      <div className="user-response-section mt-4"
                           style={{ 
                             borderLeft: '4px solid #28a745', 
                             paddingLeft: '1rem', 
                             backgroundColor: '#f8f9fa' 
                           }}>
                        <h5 className="mb-3 text-success">
                          {isGrowthPartner() ? "Course Taker's Submitted Response" : "Your Submitted Response"}
                        </h5>
                        
                        {currentLessonResponse.response_type === 'text' && (
                          <div className="text-response">
                            <p>{currentLessonResponse.response_content}</p>
                          </div>
                        )}
                        
                        {currentLessonResponse.response_type === 'file' && (
                          <div className="file-response mb-4">
                            <a 
                              href={getResponseFileUrl(currentLessonResponse)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-outline-secondary"
                            >
                              <FontAwesomeIcon icon={faFile} className="me-2" />
                              {currentLessonResponse.response_filename || "Download your file"}
                            </a>
                          </div>
                        )}
                        
                        {currentLessonResponse.response_type === 'image' && (
                          <div className="image-response mb-4">
                            <img 
                              src={getResponseFileUrl(currentLessonResponse)} 
                              alt="Your submitted response"
                              className="img-fluid rounded"
                              style={{ maxHeight: '400px' }}
                            />
                          </div>
                        )}
                        
                        {currentLessonResponse.response_type === 'audio' && (
                          <div className="audio-response mb-4">
                            <audio controls className="w-100">
                              <source src={getResponseFileUrl(currentLessonResponse)} type={currentLessonResponse.response_mimetype || "audio/mp3"} />
                              Your browser does not support the audio tag.
                            </audio>
                          </div>
                        )}
                        
                        {currentLessonResponse.response_type === 'video' && (
                          <div className="video-container mb-4">
                            <video controls className="w-100">
                              <source src={getResponseFileUrl(currentLessonResponse)} type={currentLessonResponse.response_mimetype || "video/mp4"} />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        
                        <div className="response-metadata mt-3 text-muted">
                          <small>
                            {isGrowthPartner() ? "Submitted by course taker on " : "Submitted on "}
                            {(() => {
                              const date = new Date(currentLessonResponse.completed_at);
                              return isNaN(date.getTime()) ? 'Recently' : date.toLocaleString();
                            })()}
                          </small>
                        </div>
                      </div>
                    )}
                    
                    {/* Reviewer Section - Show for lessons with review flag set to true */}
                    {(isGrowthPartner() || isLessonCompleted(activeModuleIndex, activeLesson.id)) && activeLesson.review === true && (
                      <div className="reviewer-section">
                        <h5>Reviewer Section</h5>
                        
                        {/* Show message if review already submitted */}
                        {isGrowthPartner() && hasLessonBeenReviewed(activeLesson) && (
                          <div className="alert alert-success mb-4">
                            <div className="d-flex align-items-center">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                              <div>
                                <strong>Review Submitted</strong>
                                <div>You have already reviewed this lesson. The review fields below are now disabled.</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* SIA Status Selection */}
                        <div className="mb-4">
                          <h6 className="mb-3">Partner's SIA Status</h6>
                          <p className="text-muted mb-3">Track your partner's last week SIA progress by selecting the appropriate status.</p>
                          
                          <div className="status-options">
                            <div 
                              className={`status-option green ${siaStatus === 'green' ? 'selected' : ''} ${!isGrowthPartner() || hasLessonBeenReviewed(activeLesson) ? 'disabled' : ''}`} 
                              onClick={() => isGrowthPartner() && !hasLessonBeenReviewed(activeLesson) ? handleSiaStatusChange('green') : null}
                            >
                              {siaStatus === 'green' && (
                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: '24px' }} />
                              )}
                            </div>
                            <div 
                              className={`status-option yellow ${siaStatus === 'yellow' ? 'selected' : ''} ${!isGrowthPartner() || hasLessonBeenReviewed(activeLesson) ? 'disabled' : ''}`}
                              onClick={() => isGrowthPartner() && !hasLessonBeenReviewed(activeLesson) ? handleSiaStatusChange('yellow') : null}
                            >
                              {siaStatus === 'yellow' && (
                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: '24px' }} />
                              )}
                            </div>
                            <div 
                              className={`status-option red ${siaStatus === 'red' ? 'selected' : ''} ${!isGrowthPartner() || hasLessonBeenReviewed(activeLesson) ? 'disabled' : ''}`}
                              onClick={() => isGrowthPartner() && !hasLessonBeenReviewed(activeLesson) ? handleSiaStatusChange('red') : null}
                            >
                              {siaStatus === 'red' && (
                                <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: '24px' }} />
                              )}
                            </div>
                          </div>
                          <div className="status-labels">
                            <div className="status-label">Green</div>
                            <div className="status-label">Yellow</div>
                            <div className="status-label">Red</div>
                          </div>
                          {!isGrowthPartner() && (
                            <div className="disabled-message">
                              Only the growth partner can change this status.
                            </div>
                          )}
                          {(isGrowthPartner() && hasLessonBeenReviewed(activeLesson)) && (
                            <div className="disabled-message">
                              You have already submitted a review for this lesson.
                            </div>
                          )}
                        </div>
                        
                        {/* Growth Partner Meeting Reflection */}
                        <div className="mb-4">
                          <h6 className="mb-3">Growth Partner Meeting Reflection</h6>
                          <p className="text-muted mb-3">Document what you learned from the meeting and take notes on how it went. This will help you grow and support your future course work.</p>
                          
                          <Form.Group>
                            <Form.Control 
                              as="textarea" 
                              rows={5} 
                              placeholder="Enter your reflection on the growth partner meeting..."
                              onChange={handleReflectionTextChange}
                              value={reflectionText}
                              disabled={!isGrowthPartner() || hasLessonBeenReviewed(activeLesson)}
                              className="reflection-textarea"
                            />
                            {!isGrowthPartner() && (
                              <div className="disabled-message">
                                Only the growth partner can edit this reflection.
                              </div>
                            )}
                            {(isGrowthPartner() && hasLessonBeenReviewed(activeLesson)) && (
                              <div className="disabled-message">
                                You have already submitted a review for this lesson.
                              </div>
                            )}
                          </Form.Group>
                        </div>
                        
                        {/* User Feedback Section - Show only after growth partner has reviewed */}
                        {hasLessonBeenReviewed(activeLesson) && (
                          <div className="mb-4">
                            <h6 className="mb-3">Course Taker's Response to Growth Partner</h6>
                            <p className="text-muted mb-3">Provide response to the growth partner's feedback and reflection.</p>
                            
                            <Form.Group>
                              <Form.Control 
                                as="textarea" 
                                rows={5} 
                                placeholder="Enter your response to the growth partner's feedback..."
                                onChange={handleUserFeedbackChange}
                                value={userFeedback}
                                disabled={isGrowthPartner() || hasUserFeedbackBeenSubmitted(activeLesson)}
                                className="user-feedback-textarea"
                              />
                              {isGrowthPartner() && (
                                <div className="disabled-message">
                                  Only the course taker can provide a response to the growth partner's feedback.
                                </div>
                              )}
                              {(!isGrowthPartner() && hasUserFeedbackBeenSubmitted(activeLesson)) && (
                                <div className="disabled-message">
                                  You have already submitted your response to the growth partner's feedback.
                                </div>
                              )}
                            </Form.Group>
                            
                            {/* Submit button for user feedback - Only show for course takers if feedback not yet submitted */}
                            {!isGrowthPartner() && !hasUserFeedbackBeenSubmitted(activeLesson) && (
                              <div className="d-flex justify-content-end mt-3">
                                <Button 
                                  variant="outline-primary"
                                  onClick={handleSubmitUserFeedback}
                                  disabled={submittingUserFeedback || !userFeedback.trim()}
                                  className="px-4 py-2"
                                >
                                  {submittingUserFeedback ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Submitting...
                                    </>
                                  ) : (
                                    'Submit Response'
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Submit button - Only show for growth partner if review not yet submitted */}
                        {isGrowthPartner() && !hasLessonBeenReviewed(activeLesson) && (
                          <div className="d-flex justify-content-end mt-4">
                            <Button 
                              variant="primary"
                              onClick={handleSubmitReviewerData}
                              disabled={submittingReviewerData || !siaStatus || !reflectionText.trim()}
                              className="px-4 py-2"
                            >
                              {submittingReviewerData ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Submitting...
                                </>
                              ) : (
                                'Submit Responses'
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Lesson navigation */}
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleGoToLesson(Math.max(0, activeLessonIndex - 1))}
                        disabled={activeLessonIndex === 0}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Previous Lesson
                      </Button>

                      {activeLessonIndex === activeModule.lessons.length - 1 ? (
                        <Button
                          variant="primary"
                          onClick={handleBackToModuleOverview}
                        >
                          Back to Module
                          <FontAwesomeIcon icon={faList} className="ms-2" />
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => handleGoToLesson(activeLessonIndex + 1)}
                        >
                          Next Lesson
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* Growth Partner Selection Modal */}
      <Modal 
        open={showGrowthPartnerModal} 
        onClose={handleCloseGrowthPartnerModal}
        showCloseIcon={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "growthPartnerModal",
        }}
      >
        <div className="growthPartnerModalContent">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="growthPartnerModalHeader">Select Growth Partner</h5>
            <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={handleCloseGrowthPartnerModal} />
          </div>
          {loadingTeamMembers ? (
            <div className="text-center py-4">
              <Loader />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-0">No team members found. You are the only member in this team.</p>
            </div>
          ) : (
            <div className="team-members-list">
              {teamMembers.map(member => (
                <div 
                  key={member.id} 
                  className={`team-member-item d-flex align-items-center p-3 mb-2 rounded ${selectedGrowthPartner && String(selectedGrowthPartner.id) === String(member.id) ? 'bg-light border' : ''}`}
                  onClick={() => handleGrowthPartnerSelect(member)}
                >
                  <div className="me-3">
                    <img 
                      src={member.avatar} 
                      alt={member.name || member.full_name}
                      className="rounded-circle team-member-avatar"
                      style={{ width: "40px", height: "40px", objectFit: "cover" }}
                    />
                  </div>
                  <div>
                    <div className="fw-bold">{member.name || member.full_name}</div>
                    <div className="text-muted small">
                      {member.role_id === 2 ? 'Super Admin' : 
                       member.role_id === 3 ? 'Admin' :
                       member.role_id === 4 ? 'Member' : 'User'}
                    </div>
                  </div>
                  {selectedGrowthPartner && String(selectedGrowthPartner.id) === String(member.id) && (
                    <div className="ms-auto">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Lesson Response Visibility Toggle */}
          {selectedGrowthPartner && (
            <div className="lesson-visibility-section mt-4 p-3 bg-light rounded">
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <h6 className="mb-1 fw-semibold">Lesson Response Visibility</h6>
                  <p className="mb-0 text-muted small">
                    Allow your growth partner to view your submitted responses to lessons
                  </p>
                </div>
                <div className="form-check">
                  {loadingGrowthPartnerDetails ? (
                    <div className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      <small className="text-muted">Loading...</small>
                    </div>
                  ) : (
                    <>
                      <input
                        className="form-check-input lesson-visibility-checkbox"
                        type="checkbox"
                        id="lessonResponseVisibility"
                        checked={canViewLessonResponses}
                        onChange={(e) => setCanViewLessonResponses(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="lessonResponseVisibility">
                        <span className="visually-hidden">Toggle lesson response visibility</span>
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="d-flex justify-content-end mt-3">
            <Button 
              variant="primary" 
              onClick={handleConfirmGrowthPartner}
              disabled={!selectedGrowthPartner || loadingTeamMembers || loadingGrowthPartnerDetails}
            >
              Confirm Selection
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserCourseView; 